import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";

import { EmployeePredictionDetail } from "../Types/MLAnalyticsTypes";

interface KeyInsightsPanelProps {
  detail: EmployeePredictionDetail;
  teamAverages: Record<string, number | null | undefined>;
}

type InsightStatus = "Good" | "Moderate" | "Low" | "High";

interface InsightRowConfig {
  key: string;
  label: string;
  field: keyof EmployeePredictionDetail;
  /** Whether a higher raw value is a better outcome (false for issue/defect counts). */
  higherIsBetter: boolean;
  format: (value: number | null | undefined) => string;
}

const asPercentage = (value: number | null | undefined): string =>
  value === null || value === undefined || Number.isNaN(value) ? "—" : `${value.toFixed(1)}%`;

const asRatioPercentage = (value: number | null | undefined): string =>
  value === null || value === undefined || Number.isNaN(value) ? "—" : `${(value * 100).toFixed(1)}%`;

const asRating = (value: number | null | undefined): string =>
  value === null || value === undefined || Number.isNaN(value) ? "—" : `${value.toFixed(2)} / 5`;

const asCount = (value: number | null | undefined): string =>
  value === null || value === undefined || Number.isNaN(value) ? "—" : `${Math.round(value)}`;

const INSIGHT_ROWS: InsightRowConfig[] = [
  {
    key: "avg_peer_rating_filled",
    label: "Peer Review Score",
    field: "avg_peer_rating_filled",
    higherIsBetter: true,
    format: asRating,
  },
  {
    key: "noncompliance_issue_count_filled",
    label: "Non-Compliance Issues",
    field: "noncompliance_issue_count_filled",
    higherIsBetter: false,
    format: asCount,
  },
  {
    key: "attendance_percentage_filled",
    label: "Attendance",
    field: "attendance_percentage_filled",
    higherIsBetter: true,
    format: asPercentage,
  },
  {
    key: "kpi_achievement_percentage_filled",
    label: "KPI Achievement",
    field: "kpi_achievement_percentage_filled",
    higherIsBetter: true,
    format: asPercentage,
  },
  {
    key: "on_time_delivery_percentage_filled",
    label: "On-time Delivery",
    field: "on_time_delivery_percentage_filled",
    higherIsBetter: true,
    format: asPercentage,
  },
  {
    key: "training_completion_ratio_filled",
    label: "Training Completion",
    field: "training_completion_ratio_filled",
    higherIsBetter: true,
    format: asRatioPercentage,
  },
];

const STATUS_COLOR: Record<InsightStatus, "success" | "warning" | "error"> = {
  Good: "success",
  Moderate: "warning",
  Low: "error",
  High: "error",
};

const computeStatus = (
  employeeValue: number | null | undefined,
  teamAverage: number | null | undefined,
  higherIsBetter: boolean
): InsightStatus => {
  if (employeeValue === null || employeeValue === undefined || teamAverage === null || teamAverage === undefined) {
    return "Moderate";
  }

  const diff = employeeValue - teamAverage;

  if (higherIsBetter) {
    if (diff >= 0) return "Good";
    const threshold = Math.abs(teamAverage) * 0.15 || 0.5;
    return diff < -threshold ? "Low" : "Moderate";
  }

  if (diff <= 0) return "Good";
  const threshold = Math.abs(teamAverage) * 0.5 || 1;
  return diff > threshold ? "High" : "Moderate";
};

/** Right-column panel comparing an employee's real-data key insights against their team average. */
const KeyInsightsPanel: React.FC<KeyInsightsPanelProps> = ({ detail, teamAverages }) => {
  return (
    <Stack spacing={2}>
      {INSIGHT_ROWS.map((row) => {
        const employeeValue = detail[row.field] as number | null | undefined;
        const teamAverage = teamAverages[row.key];
        const status = computeStatus(employeeValue, teamAverage, row.higherIsBetter);

        return (
          <Box
            key={row.key}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1,
              px: 1.5,
              borderRadius: 1,
              bgcolor: "grey.50",
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {row.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Team avg: {row.format(teamAverage)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body1" fontWeight={700}>
                {row.format(employeeValue)}
              </Typography>
              <Chip size="small" color={STATUS_COLOR[status]} label={status} />
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export default KeyInsightsPanel;
